import { SelectChangeEvent } from '@mui/material';
import {
  AdditionalBooleanFieldKey,
  AdditionalFieldKey,
  AdditionalFieldsModel,
} from '../model';
import { PeriodErrors, validatePeriod } from '../utils/validatePeriod';
import type { SaveFieldOptions } from './useAdditionalFieldsPersistence';

type SaveField = (
  key: AdditionalFieldKey,
  valueOverride?: AdditionalFieldsModel[AdditionalFieldKey],
  options?: SaveFieldOptions,
) => Promise<void>;

type DebouncedSaveField = (
  key: AdditionalFieldKey,
  value: AdditionalFieldsModel[AdditionalFieldKey],
) => void;

type UseAdditionalFieldsHandlersParams = {
  values: AdditionalFieldsModel;
  setValues: React.Dispatch<React.SetStateAction<AdditionalFieldsModel>>;
  setPeriodErrors: React.Dispatch<React.SetStateAction<PeriodErrors>>;
  saveField: SaveField;
  debouncedSaveField: DebouncedSaveField;
};

type UseAdditionalFieldsHandlersResult = {
  handleTextChange: (
    key: Extract<
      AdditionalFieldKey,
      | 'agencyName'
      | 'clientName'
      | 'campaignName'
      | 'statsUrl'
      | 'verifiers'
      | 'gridArchiveId'
    >,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleNumberKeyDown: (
    key: Extract<
      AdditionalFieldKey,
      | 'volume'
      | 'clientCpm'
      | 'clientBudget'
      | 'returnDiscountPercent'
      | 'payCpm'
      | 'payBudget'
      | 'adFeePercent'
      | 'adFeeSum'
    >,
  ) => (event: React.KeyboardEvent<HTMLInputElement>) => Promise<void>;
  handleTextKeyDown: (
    key: Extract<
      AdditionalFieldKey,
      | 'agencyName'
      | 'clientName'
      | 'campaignName'
      | 'statsUrl'
      | 'verifiers'
      | 'gridArchiveId'
    >,
  ) => (event: React.KeyboardEvent<HTMLInputElement>) => Promise<void>;
  handleSelectChange: (
    key: Extract<
      AdditionalFieldKey,
      | 'format'
      | 'billing'
      | 'trafficType'
      | 'product'
      | 'category'
      | 'ord'
      | 'materialType'
    >,
  ) => (event: SelectChangeEvent<string>) => Promise<void>;
  handleMultiselectChange: (
    key: Extract<
      AdditionalFieldKey,
      'amParticipants' | 'tmParticipants' | 'cmParticipants'
    >,
  ) => (_: React.SyntheticEvent, newValue: string[]) => Promise<void>;
  handleSelectClear: (
    key: Extract<
      AdditionalFieldKey,
      | 'format'
      | 'billing'
      | 'trafficType'
      | 'product'
      | 'category'
      | 'ord'
      | 'materialType'
    >,
  ) => () => Promise<void>;
  handleDateChange: (
    key: Extract<AdditionalFieldKey, 'periodStart' | 'periodEnd'>,
  ) => (date: Date | null) => Promise<void>;
  handleDateClear: (
    key: Extract<AdditionalFieldKey, 'periodStart' | 'periodEnd'>,
  ) => () => Promise<void>;
  handleBooleanChange: (
    key: AdditionalBooleanFieldKey,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleBlur: (key: AdditionalFieldKey) => () => Promise<void>;
};

export function useAdditionalFieldsHandlers({
  values,
  setValues,
  setPeriodErrors,
  saveField,
  debouncedSaveField,
}: UseAdditionalFieldsHandlersParams): UseAdditionalFieldsHandlersResult {
  const handleTextChange =
    (
      key: Extract<
        AdditionalFieldKey,
        | 'agencyName'
        | 'clientName'
        | 'campaignName'
        | 'statsUrl'
        | 'verifiers'
        | 'gridArchiveId'
      >,
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setValues((prev) => ({
        ...prev,
        [key]: newValue,
      }));
      debouncedSaveField(key, newValue);
    };

  const handleNumberKeyDown =
    (
      key: Extract<
        AdditionalFieldKey,
        | 'volume'
        | 'clientCpm'
        | 'clientBudget'
        | 'returnDiscountPercent'
        | 'payCpm'
        | 'payBudget'
        | 'adFeePercent'
        | 'adFeeSum'
      >,
    ) =>
    async (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      try {
        await saveField(key);
      } catch {
        // ignore, error will be handled globally
      }
    };

  const handleTextKeyDown =
    (
      key: Extract<
        AdditionalFieldKey,
        | 'agencyName'
        | 'clientName'
        | 'campaignName'
        | 'statsUrl'
        | 'verifiers'
        | 'gridArchiveId'
      >,
    ) =>
    async (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      try {
        await saveField(key, undefined, {
          addToDictionary: key === 'agencyName' || key === 'clientName',
        });
      } catch {
        // ignore, error will be handled globally
      }
    };

  const handleSelectChange =
    (
      key: Extract<
        AdditionalFieldKey,
        | 'format'
        | 'billing'
        | 'trafficType'
        | 'product'
        | 'category'
        | 'ord'
        | 'materialType'
      >,
    ) =>
    async (event: SelectChangeEvent<string>) => {
      const value = event.target.value || null;
      setValues((prev) => ({
        ...prev,
        [key]: value,
      }));
      try {
        await saveField(
          key,
          value as AdditionalFieldsModel[AdditionalFieldKey],
        );
      } catch {
        // ignore, error will be handled globally
      }
    };

  const handleMultiselectChange =
    (
      key: Extract<
        AdditionalFieldKey,
        'amParticipants' | 'tmParticipants' | 'cmParticipants'
      >,
    ) =>
    async (_: React.SyntheticEvent, newValue: string[]) => {
      setValues((prev) => ({
        ...prev,
        [key]: newValue.length > 0 ? newValue : null,
      }));
      try {
        await saveField(
          key,
          newValue.length > 0
            ? newValue
            : (null as AdditionalFieldsModel[AdditionalFieldKey]),
        );
      } catch {
        // ignore, error will be handled globally
      }
    };

  const handleSelectClear =
    (
      key: Extract<
        AdditionalFieldKey,
        | 'format'
        | 'billing'
        | 'trafficType'
        | 'product'
        | 'category'
        | 'ord'
        | 'materialType'
      >,
    ) =>
    async () => {
      setValues((prev) => ({
        ...prev,
        [key]: '',
      }));
      try {
        await saveField(key, '');
      } catch {
        // ignore, error will be handled globally
      }
    };

  const handleDateChange =
    (key: Extract<AdditionalFieldKey, 'periodStart' | 'periodEnd'>) =>
    async (date: Date | null) => {
      const isValidDate = date instanceof Date && !Number.isNaN(date.getTime());

      const ISO = isValidDate
        ? [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, '0'),
            String(date.getDate()).padStart(2, '0'),
          ].join('-')
        : null;

      const newValues = {
        ...values,
        [key]: ISO,
      };

      const errors = validatePeriod(newValues.periodStart, newValues.periodEnd);
      setPeriodErrors(errors);

      setValues(newValues);

      if (errors.periodStart || errors.periodEnd) {
        return;
      }

      try {
        const previousErrors = validatePeriod(
          values.periodStart,
          values.periodEnd,
        );
        await saveField(
          key,
          ISO,
          previousErrors.periodStart ? { period: newValues } : undefined,
        );
      } catch {
        // ignore, error will be handled globally
      }
    };

  const handleDateClear =
    (key: Extract<AdditionalFieldKey, 'periodStart' | 'periodEnd'>) => () =>
      handleDateChange(key)(null);

  const handleBooleanChange =
    (key: AdditionalBooleanFieldKey) =>
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setValues((prev) => ({
        ...prev,
        [key]: checked,
      }));
      try {
        await saveField(key, checked);
      } catch {
        // ignore, error will be handled globally
      }
    };

  const handleBlur = (key: AdditionalFieldKey) => async () => {
    try {
      await saveField(key, undefined, {
        addToDictionary: key === 'agencyName' || key === 'clientName',
      });
    } catch {
      // тихо игнорируем, глобальный errorHandler отработает на уровне RPC
    }
  };

  return {
    handleTextChange,
    handleNumberKeyDown,
    handleTextKeyDown,
    handleSelectChange,
    handleMultiselectChange,
    handleSelectClear,
    handleDateChange,
    handleDateClear,
    handleBooleanChange,
    handleBlur,
  };
}
